import { ApolloError } from '@apollo/client';
import * as Sentry from '@sentry/react';
import QueryString from 'qs';
import React, { useContext, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

import {
  GetPartnerBrandingQuery,
  useGetPartnerBrandingForApplicationQuery,
  useGetPartnerBrandingQuery
} from '../../generated/graphql';
import foxquiltBrand from '../../images/brand.png';
import foxquiltLogo from '../../images/logo.png';
import { getPartnerByCode } from '../../utils/partners';
import { Context, ContextValue } from '../../utils/store';
import ModalWithHeader from '../ModalWithHeader';
import ProgressBar from '../Navigation/ProgressBar';
import HelpText from './HelpText';

const OBJECT_ID = /^[a-fA-F0-9]{24}$/;

type Branding = NonNullable<GetPartnerBrandingQuery['getPartnerBranding']>;
type ShownLogo = { name: string; logo: string };

// A backend that does not know the query yet, an older stage or the deploy
// window before forms-backend ships, rejects it as invalid. That is "no
// record", not "don't know": nothing there could have been switched off.
const UNKNOWN_QUERY_CODE = 'GRAPHQL_VALIDATION_FAILED';
// Apollo Server answers a validation failure with HTTP 400, which Apollo
// Client files under networkError with the GraphQL errors in result.errors.
type ServerErrorLike = {
  result?: { errors?: { extensions?: { code?: string } }[] };
};
const rejectedAsUnknown = (error?: ApolloError) => {
  const inBody = error?.graphQLErrors ?? [];
  const inNetwork =
    (error?.networkError as ServerErrorLike | null | undefined)?.result
      ?.errors ?? [];
  return [...inBody, ...inNetwork].some(
    (e) => e?.extensions?.code === UNKNOWN_QUERY_CODE
  );
};

// undefined while loading or after a failure; null when the lookup ran and
// found nothing; otherwise the record.
function settled<T>(
  result: { loading: boolean; data?: T; error?: ApolloError },
  pick: (data: T) => Branding | null | undefined
): Branding | null | undefined {
  if (result.loading) return undefined;
  if (result.data) return pick(result.data);
  return rejectedAsUnknown(result.error) ? null : undefined;
}

// Routes that carry an application id, so the partner can be read back from
// the application: /quote/<groupId>, /<groupId>, /GL/<id> and /EO/<id>. The
// last three are where a refresh lands, with the query string long gone.
const applicationIdFromPath = (pathname: string): string | null => {
  const [, first, second] = pathname.split('/');
  const id =
    first === 'quote' || first === 'GL' || first === 'EO' ? second : first;
  return id && OBJECT_ID.test(id) ? id : null;
};

const Header: React.FC = () => {
  const context: ContextValue | null = useContext(Context);
  const location = useLocation();

  const headerTitle = context ? context.headerTitle : '';
  const progress = context ? context.progress : 0;
  const isCustomerAgreementActive = context
    ? context.isCustomerAgreementActive
    : false;
  const [helperShowing, setHelperShowing] = useState(false);
  const [failedLogoUrl, setFailedLogoUrl] = useState<string | null>(null);

  const query = QueryString.parse(location.search, { ignoreQueryPrefix: true });

  const partnerCodeFromUrl =
    typeof query.partnercode === 'string'
      ? query.partnercode.trim().toLowerCase() || null
      : null;
  // Broker links carry the Agency _id, which settles a code two agencies share.
  const partnerAgencyIdFromUrl =
    typeof query.agencyId === 'string' && OBJECT_ID.test(query.agencyId)
      ? query.agencyId
      : null;

  const contextPartnerCode = context?.partnerCode;
  const contextPartnerAgencyId = context?.partnerAgencyId;
  const setPartnerCode = context?.setPartnerCode;
  const setPartnerAgencyId = context?.setPartnerAgencyId;
  // Remembered for later pages, which no longer carry the query string.
  useEffect(() => {
    if (!setPartnerCode || !setPartnerAgencyId || !partnerCodeFromUrl) return;
    if (partnerCodeFromUrl !== contextPartnerCode) {
      setPartnerCode(partnerCodeFromUrl);
    }
    if (partnerAgencyIdFromUrl !== contextPartnerAgencyId) {
      setPartnerAgencyId(partnerAgencyIdFromUrl);
    }
  }, [
    partnerCodeFromUrl,
    partnerAgencyIdFromUrl,
    contextPartnerCode,
    contextPartnerAgencyId,
    setPartnerCode,
    setPartnerAgencyId
  ]);

  const partnerCode = partnerCodeFromUrl ?? contextPartnerCode ?? null;
  // A code from the link brings its own id, or none: never a remembered one.
  const partnerAgencyId = partnerCodeFromUrl
    ? partnerAgencyIdFromUrl
    : contextPartnerAgencyId ?? null;

  const applicationId = applicationIdFromPath(location.pathname);

  const byCode = useGetPartnerBrandingQuery({
    variables: { partnercode: partnerCode ?? '', agencyId: partnerAgencyId },
    skip: !partnerCode,
    // A branding lookup must never surface as an application error.
    errorPolicy: 'all'
  });
  // Emailed quote links carry no partner code, so pages that name an
  // application read the partner back from it; that also keeps the branding
  // out of the link.
  const byApplication = useGetPartnerBrandingForApplicationQuery({
    variables: { id: applicationId ?? '' },
    skip: !applicationId,
    errorPolicy: 'all'
  });

  // `/quote/` with the slash, so `/quote-expired` stays an application page.
  const onQuoteScreen = location.pathname.startsWith('/quote/');

  const fromRecord = (record: Branding): ShownLogo | null => {
    const wanted = onQuoteScreen
      ? record.showOnQuoteScreen
      : record.showOnApplication;
    if (!wanted) return null;
    if (record.logoUrl) {
      return { name: record.partnerName, logo: record.logoUrl };
    }
    // No upload yet: the built-in image stands in, under the same switches.
    return record.hasLogo ? null : getPartnerByCode(record.partnerCode);
  };

  // Only "no agency" may fall back to the built-in list; a failure means
  // "don't know", and painting a logo then could show one an admin had
  // switched off.
  const fromCode = (): ShownLogo | null => {
    if (!partnerCode) return null;
    const record = settled(byCode, (data) => data.getPartnerBranding);
    if (record === undefined) return null;
    return record === null ? getPartnerByCode(partnerCode) : fromRecord(record);
  };

  let partner: ShownLogo | null;
  if (applicationId) {
    const record = settled(
      byApplication,
      (data) => data.getPartnerBrandingForApplication
    );
    if (byApplication.loading) {
      // The remembered code almost always names the same partner, and its
      // answer is already cached, so the logo does not blink on navigation.
      partner = fromCode();
    } else if (record === undefined) {
      partner = null;
    } else if (record === null) {
      partner = fromCode();
    } else {
      partner = fromRecord(record);
    }
  } else {
    partner = fromCode();
  }

  // A signed link can die before the browser asks for it: the object moved, or
  // the signature expired. Show Foxquilt alone, never the alt text.
  const shownPartner =
    partner && partner.logo !== failedLogoUrl ? partner : null;

  const reportFailedLogo = (failed: ShownLogo) => {
    setFailedLogoUrl(failed.logo);
    Sentry.withScope((scope) => {
      scope.setExtras({ partner: failed.name, path: location.pathname });
      Sentry.captureMessage('Partner logo failed to load');
    });
  };

  return (
    <div className="sticky z-10 w-full top-0 p-0 m-0" data-testid="Header">
      <div className="shadow-lg bg-white" data-testid="Header">
        <div className="w-full md:h-20 h-14 md:px-0 px-6 md:py-0 py-2">
          <nav className="flex justify-between items-center w-full h-full">
            <div className="flex md:pl-20 md:h-16">
              <Link to="/">
                <div className="flex w-full h-full">
                  <div className="md:pt-1 lg:w-11 md:w-9">
                    <img
                      className="md:w-full"
                      src={foxquiltLogo}
                      alt="company-logo"
                    />
                  </div>
                  <div className="ml-2 lg:w-24 w-20 flex items-center">
                    <img
                      className="w-full"
                      src={foxquiltBrand}
                      alt="company-logo"
                    />
                  </div>
                  {shownPartner ? (
                    <>
                      <div className="mx-3 self-center h-8 w-px bg-gray-300" />
                      <div className="lg:w-32 w-24 flex items-center">
                        <img
                          className="w-full max-h-10 object-contain"
                          src={shownPartner.logo}
                          alt={
                            shownPartner.name
                              ? `${shownPartner.name} logo`
                              : 'Partner logo'
                          }
                          data-testid="partner-logo"
                          onError={() => reportFailedLogo(shownPartner)}
                        />
                      </div>
                    </>
                  ) : null}
                </div>
              </Link>
            </div>
            {headerTitle ? (
              <div className="text-3xl hidden md:block pr-24">
                <span className="text-navy-dark font-bold">
                  {isCustomerAgreementActive
                    ? 'Client Agreements'
                    : 'Tailored Quote For'}
                  :{' '}
                </span>
                <span className="font-medium ml-1">{headerTitle}</span>
              </div>
            ) : null}
            <div id="helpMenu">
              <div className="h-20 flex md:block items-center md:pr-20">
                <div
                  onMouseLeave={() => setHelperShowing(false)}
                  onMouseEnter={() => setHelperShowing(true)}
                  className="hidden md:flex absolute w-80 h-full top-0 right-0 items-center justify-end whitespace-nowrap tracking-wider font-medium text-base text-xs text-secondary underline pr-20"
                >
                  Need help?
                </div>
                <a
                  onClick={() => setHelperShowing(!helperShowing)}
                  className="block md:hidden text-secondary font-medium text-base underline"
                >
                  Need help?
                </a>
              </div>
              {helperShowing ? (
                <>
                  <div
                    onMouseEnter={() => setHelperShowing(true)}
                    onMouseLeave={() => setHelperShowing(false)}
                    className="md:block hidden absolute w-80 top-20 right-0 min-w-fit rounded-bl-xl text-base text-xs"
                  >
                    <div className="p-4 bg-gray-300 rounded-bl-xl shadow-md">
                      <HelpText></HelpText>
                    </div>
                  </div>
                  <ModalWithHeader
                    headerText="Need help?"
                    onBackgroundClick={() => setHelperShowing(false)}
                    additionalRootStyleClasses="md:hidden"
                  >
                    <HelpText></HelpText>
                  </ModalWithHeader>
                </>
              ) : null}
            </div>
          </nav>
        </div>
      </div>
      <ProgressBar percentage={progress} />
    </div>
  );
};

export default Header;
